import maplibregl from 'maplibre-gl';
import {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { usePreference } from '../../common/util/preferences';
import { map } from '../core/MapView';

const MapDefaultCamera = ({ mapReady }) => {
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  // 2ECOMP: shallowEqual evita re-renders desnecessários
  const positions = useSelector((state) => state.session.positions, shallowEqual);

  // 2ECOMP: PremiumUI Refresh — excluir devices ocultos do fit bounds
  const hiddenDeviceIds = useSelector((state) => state.devices.hiddenDeviceIds);
  const hiddenSet = useMemo(() => new Set(hiddenDeviceIds), [hiddenDeviceIds]);

  const defaultLatitude = usePreference('latitude');
  const defaultLongitude = usePreference('longitude');
  const defaultZoom = usePreference('zoom', 0);

  const [initialized, setInitialized] = useState(false);

  // Fit bounds function to center all devices
  const fitAllDevices = useCallback(() => {
    if (!map) return;
    // 2ECOMP: PremiumUI Refresh — só considerar devices visíveis no fit bounds
    const coordinates = Object.values(positions)
      .filter((item) => !hiddenSet.has(item.deviceId))
      .map((item) => [item.longitude, item.latitude]);
    if (coordinates.length > 1) {
      const bounds = coordinates.reduce(
        (bounds, item) => bounds.extend(item),
        new maplibregl.LngLatBounds(coordinates[0], coordinates[1])
      );
      const canvas = map.getCanvas();
      map.fitBounds(bounds, {
        duration: 500,
        padding: Math.min(canvas.width, canvas.height) * 0.1,
      });
    } else if (coordinates.length === 1) {
      map.flyTo({
        center: coordinates[0],
        zoom: 15,
        duration: 500,
      });
    }
  }, [positions, hiddenSet]);

  // Listen for mapFitBounds event from controls panel
  useEffect(() => {
    if (!mapReady) return;

    const handleFitBounds = () => {
      fitAllDevices();
    };

    window.addEventListener('mapFitBounds', handleFitBounds);
    return () => {
      window.removeEventListener('mapFitBounds', handleFitBounds);
    };
  }, [mapReady, fitAllDevices]);

  useEffect(() => {
    if (!mapReady || initialized) return;
    if (selectedDeviceId) {
      const position = positions[selectedDeviceId];
      if (position) {
        map.jumpTo({
          center: [position.longitude, position.latitude],
          zoom: Math.max(defaultZoom > 0 ? defaultZoom : map.getZoom(), 10),
        });
        setInitialized(true);
      }
    } else {
      if (defaultLatitude && defaultLongitude) {
        map.jumpTo({
          center: [defaultLongitude, defaultLatitude],
          zoom: defaultZoom,
        });
        setInitialized(true);
      } else {
        // 2ECOMP: PremiumUI Refresh — só considerar devices visíveis
        const coordinates = Object.values(positions)
          .filter((item) => !hiddenSet.has(item.deviceId))
          .map((item) => [item.longitude, item.latitude]);
        if (coordinates.length > 1) {
          const bounds = coordinates.reduce((bounds, item) => bounds.extend(item), new maplibregl.LngLatBounds(coordinates[0], coordinates[1]));
          const canvas = map.getCanvas();
          map.fitBounds(bounds, {
            duration: 0,
            padding: Math.min(canvas.width, canvas.height) * 0.1,
          });
          setInitialized(true);
        } else if (coordinates.length) {
          const [individual] = coordinates;
          map.jumpTo({
            center: individual,
            zoom: Math.max(defaultZoom > 0 ? defaultZoom : map.getZoom(), 10),
          });
          setInitialized(true);
        }
      }
    }
  }, [selectedDeviceId, initialized, defaultLatitude, defaultLongitude, defaultZoom, positions, hiddenSet, mapReady]);

  return null;
};

MapDefaultCamera.handlesMapReady = true;

export default MapDefaultCamera;
